// frontend/src/components/MyLinkJobPopup.js
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Accordion } from 'react-bootstrap';
import audioNotificationService from '../services/audioNotificationService';

// Helper function to format date/time
const formatWhen = (iso) => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      weekday: 'short', day: 'numeric', month: 'short',
      timeZone: 'Europe/London'
    }).formatToParts(date);
    const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
    return `${p.hour}:${p.minute} ${p.dayPeriod?.toLowerCase()}, ${p.weekday} ${p.day} ${p.month}`;
  } catch (error) {
    return 'Invalid Date';
  }
};

const MyLinkJobPopup = ({ 
  allJobs = [],
  onAccept, 
  onReviewLater, 
  onClose
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeKey, setActiveKey] = useState(null); // Start with accordion closed
  const audioPlayedRef = useRef(false);

  // Play audio only once when popup first appears
  useEffect(() => {
    if (allJobs.length > 0 && !audioPlayedRef.current) {
      console.log('🔊 Playing audio for MyLink job popup (first time only)');
      const playAudio = async () => {
        try {
          const isASAP = allJobs[0]?.isASAP || false;
          await audioNotificationService.playNewJobAudio(isASAP ? 'asap' : 'mylink');
          console.log('✅ Audio played successfully');
          audioPlayedRef.current = true;
        } catch (error) {
          console.error('❌ Failed to play audio warning:', error);
        }
      };
      playAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allJobs.length]); // Only trigger when jobs first appear

  // No need to track currentIndex - accordion handles navigation

  const handleAccept = async (jobId) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await onAccept(jobId);
    } catch (error) {
      console.error('Failed to accept job:', error);
      setIsProcessing(false);
    }
  };

  const handleReviewLater = () => {
    onClose();
  };

  if (!allJobs || allJobs.length === 0) {
    return null;
  }

  // Helper to extract towns from addresses
  const extractTowns = (from, to) => {
    const fromParts = from ? from.split(',').map(s => s.trim()) : [];
    const toParts = to ? to.split(',').map(s => s.trim()) : [];
    const fromTown = fromParts[fromParts.length - 1] || from;
    const toTown = toParts[toParts.length - 1] || to;
    return `${fromTown} → ${toTown}`;
  };

  return (
    <>
      <style>{`
        .mylink-job-popup .modal-dialog {
          width: 90vw !important;
          max-width: 90vw !important;
          max-height: 90vh !important;
        }
        .mylink-job-popup .modal-content {
          max-height: 90vh !important;
          display: flex;
          flex-direction: column;
        }
        .mylink-job-popup .modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 15px;
        }
        .mylink-job-popup .modal-header {
          padding: 8px 15px;
          min-height: 40px;
        }
        .mylink-job-popup .modal-footer {
          padding: 10px 15px;
        }
      `}</style>
      <Modal
        show={allJobs.length > 0}
        onHide={handleReviewLater}
        backdrop="static"
        keyboard={false}
        centered
        className="mylink-job-popup"
        style={{ zIndex: 10000 }}
      >
      <Modal.Header 
        style={{ 
          background: allJobs[0]?.isASAP ? 'linear-gradient(135deg, #dc3545, #c82333)' : 'linear-gradient(135deg, #28a745, #20c997)',
          color: 'white',
          borderBottom: 'none',
          padding: '8px 15px'
        }}
      >
        <Modal.Title style={{ 
          fontSize: '14px', 
          fontWeight: 'bold', 
          margin: 0,
          lineHeight: '1.2',
          width: '100%',
          textAlign: 'center'
        }}>
          {allJobs[0]?.isASAP ? 'URGENT: ' : ''}MyLink Jobs ({allJobs.length})
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
        <Accordion activeKey={activeKey} onSelect={(e) => setActiveKey(e)}>
          {allJobs.map((job, idx) => {
            const timingInfo = job.isASAP ? 
              'ASAP - URGENT' : 
              (job.dateTime ? formatWhen(job.dateTime) : 'ASAP');
            
            return (
              <Accordion.Item eventKey={String(idx)} key={job.leadId || job._id}>
                <Accordion.Header>
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex align-items-center gap-2">
                      <strong>{timingInfo}</strong>
                    </div>
                    <small>{extractTowns(job.from, job.to)}</small>
                    {job.customerName && (
                      <small className="text-muted">{job.customerName}</small>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '5px 0' }}>
                      <strong>From:</strong> {job.from}
                    </p>
                    <p style={{ margin: '5px 0' }}>
                      <strong>To:</strong> {job.to}
                    </p>
                    {job.customerName && (
                      <p style={{ margin: '5px 0' }}>
                        <strong>Customer:</strong> {job.customerName}
                      </p>
                    )}
                    {job.passengers && (
                      <p style={{ margin: '5px 0' }}>
                        <strong>Passengers:</strong> {job.passengers}
                      </p>
                    )}
                    {job.notes && (
                      <div style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', borderRadius: '5px' }}>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Notes:</strong> {job.notes}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action buttons on one line */}
                  <div className="d-flex gap-2 w-100 mt-3">
                    <Button
                      variant="secondary"
                      onClick={() => handleReviewLater()}
                      className="flex-fill"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                    >
                      <span>⏰</span> Review Later
                    </Button>
                    <Button
                      variant={job.isASAP ? 'danger' : 'success'}
                      onClick={() => handleAccept(job.leadId || job._id)}
                      disabled={isProcessing}
                      className="flex-fill"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                    >
                      {isProcessing ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <>
                          <span>✓</span> Accept
                        </>
                      )}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Modal.Body>
    </Modal>
    </>
  );
};

// Helper functions to manage unprocessed jobs in localStorage
export const saveUnprocessedJob = (jobData) => {
  try {
    const unprocessed = getUnprocessedJobs();
    // Check if job already exists
    const existingIndex = unprocessed.findIndex(j => 
      (j.leadId || j._id) === (jobData.leadId || jobData._id)
    );
    
    if (existingIndex >= 0) {
      // Update existing job
      unprocessed[existingIndex] = {
        ...jobData,
        receivedAt: unprocessed[existingIndex].receivedAt || new Date().toISOString()
      };
    } else {
      // Add new job
      unprocessed.push({
        ...jobData,
        receivedAt: new Date().toISOString()
      });
    }
    
    localStorage.setItem('mylink_unprocessed_jobs', JSON.stringify(unprocessed));
    console.log('💾 Saved unprocessed MyLink job:', jobData.leadId || jobData._id);
    console.log('💾 Total unprocessed jobs now:', unprocessed.length);
    // Verify it was saved
    const verify = localStorage.getItem('mylink_unprocessed_jobs');
    console.log('✅ Verification - localStorage contains:', verify ? JSON.parse(verify).length : 0, 'job(s)');
  } catch (error) {
    console.error('Failed to save unprocessed job:', error);
  }
};

export const getUnprocessedJobs = () => {
  try {
    const stored = localStorage.getItem('mylink_unprocessed_jobs');
    if (!stored) {
      console.log('📋 No unprocessed jobs in localStorage');
      return [];
    }
    const jobs = JSON.parse(stored);
    console.log(`📋 Retrieved ${jobs.length} unprocessed job(s) from localStorage:`, jobs.map(j => ({
      leadId: j.leadId || j._id,
      from: j.from,
      to: j.to,
      receivedAt: j.receivedAt
    })));
    return jobs;
  } catch (error) {
    console.error('❌ Failed to get unprocessed jobs:', error);
    return [];
  }
};

export const removeUnprocessedJob = (jobId) => {
  try {
    const unprocessed = getUnprocessedJobs();
    const filtered = unprocessed.filter(j => 
      (j.leadId || j._id) !== jobId
    );
    localStorage.setItem('mylink_unprocessed_jobs', JSON.stringify(filtered));
    console.log('🗑️ Removed unprocessed MyLink job:', jobId);
  } catch (error) {
    console.error('Failed to remove unprocessed job:', error);
  }
};

export const clearAllUnprocessedJobs = () => {
  try {
    localStorage.removeItem('mylink_unprocessed_jobs');
    console.log('🗑️ Cleared all unprocessed MyLink jobs');
  } catch (error) {
    console.error('Failed to clear unprocessed jobs:', error);
  }
};

export default MyLinkJobPopup;

